import type { Encounter } from "@elation/api-client/src/stitch-web/models/encounter";
import { EncounterStatus } from "@elation/api-client/src/stitch-web/models/encounterStatus";

import { EncounterEntity } from "../domain";

export const NOTE_GENERATION_POLL_INTERVAL_MS = 2000;

type MaybeEncounter = Encounter | null | undefined;

/**
 * React Query `refetchInterval` while waiting for note generation.
 *
 * Continues when the encounter actor reports an active finish lifecycle, or
 * when the fetched encounter is still awaiting a note (finalizing /
 * completed+null on direct loads). Stops once a finish-versioned note exists,
 * the encounter is closed (no pending generation), or the encounter query
 * has errored (avoids indefinite polling on hard failures; the visit route
 * exposes Try again for recovery).
 */
export function noteGenerationPollIntervalMs(
  encounter: MaybeEncounter,
  {
    localOrActorActive,
    hasQueryError = false,
    completionBaseline = null,
  }: {
    localOrActorActive: boolean;
    hasQueryError?: boolean;
    completionBaseline?: string | null;
  },
): typeof NOTE_GENERATION_POLL_INTERVAL_MS | false {
  if (hasQueryError) return false;
  if (encounter?.status === EncounterStatus.closed) return false;

  if (localOrActorActive) {
    if (
      EncounterEntity.hasGeneratedNoteForFinish(encounter, completionBaseline)
    ) {
      return false;
    }
    return NOTE_GENERATION_POLL_INTERVAL_MS;
  }

  if (EncounterEntity.isAwaitingGeneratedNote(encounter)) {
    return NOTE_GENERATION_POLL_INTERVAL_MS;
  }

  return false;
}
